import { useSetAtom } from 'jotai';
import { z } from 'zod';

import * as auth from '@/services/auth';
import { handleRevokedToken } from '@/services/auth';
import { getCurrentUserV2 } from '@/services/getCurrentUserV2';
import {
  actAsUserIdAtom,
  store,
  authenticatedUserIdAtom,
  authStateResolvedAtom,
} from '@/services/globalState';
import {
  extractUserAttributesFromClaims,
  isOAuthUser,
  parseIdTokenClaims,
} from '@/services/session';
import { classifyResolveError, RESOLVE_RETRY_DELAY_MS } from '@/util/authResolveError';
import { isRevokedTokenError, parseAuthError } from '@/util/authTokens';
import { clearImpersonation, loadImpersonation } from '@/util/impersonationStorage';
import { queryClient } from '@/util/queryClient';
import { Sentry } from '@/util/sentry';

export const cognitoUserSchema = z.object({
  userId: z.string(),
  sub: z.string().optional(),
  email: z.string().email().optional(),
  lastName: z.string().optional(),
  firstName: z.string().optional(),
  identifiesAsDr: z
    .string()
    .transform((val) => val === 'true')
    .optional(),
});

/**
 * Thrown when a password user authenticates but no numeric application user id can be
 * resolved (neither `custom:user_id` on the Cognito token nor a successful `/v2/users/me`
 * lookup). The rest of the app keys API and cache state on numeric ids, so falling back
 * to a Cognito `sub` UUID would silently break subsequent requests. The login screen
 * catches this, signs the user out, and shows an error toast directing them to support.
 */
export class MissingUserIdError extends Error {
  readonly missingUserIdMeta: { sub?: string; email?: string };

  constructor(meta: { sub?: string; email?: string }) {
    super('Cognito user has no custom:user_id and /v2/users/me lookup did not return an id');

    this.name = 'MissingUserIdError';

    this.missingUserIdMeta = meta;
  }
}

export type CognitoUser = z.infer<typeof cognitoUserSchema>;

/**
 * Discriminated result type for `useAuth().login`. Callers must handle every variant —
 * silently treating an unknown next step as success is what previously let the user fall
 * through into a broken authenticated state.
 *
 * `login()` is atomic: when it returns `{ type: 'success' }`, the userIdAtom is set, the
 * `/v2/users/me` cache is populated, and the user is fully authenticated. Failure variants
 * mean the Cognito session has been cleaned up via `signOutLocal` so the caller is free
 * to navigate to /login or stay in place — no half-authenticated limbo to recover from.
 */
export type LoginResult =
  | { type: 'success'; user: CognitoUser }
  | { type: 'newPasswordRequired' }
  | { type: 'unsupportedStep'; step: string }
  | { type: 'transientFailure' }
  | { type: 'persistentFailure' };

export function useAuth() {
  const setUserId = useSetAtom(authenticatedUserIdAtom);

  const setAuthStateResolved = useSetAtom(authStateResolvedAtom);

  const getCurrentUserId = () => store.get(authenticatedUserIdAtom);

  /**
   * Finalize a cold-load auth session: write the authenticated user atom, restore any
   * persisted support-account impersonation that still belongs to this user and is
   * within its idle TTL, then mark auth state as resolved. Use this when hydrating
   * an existing Cognito session at app boot — never on a fresh `login()` call.
   */
  const finalizeColdLoadSession = async (userId: string | undefined) => {
    if (userId) {
      setUserId(userId);

      const restoredActAsUserId = await loadImpersonation(userId);

      if (restoredActAsUserId) {
        store.set(actAsUserIdAtom, restoredActAsUserId);
      }
    } else {
      setUserId(undefined);

      store.set(actAsUserIdAtom, undefined);

      await clearImpersonation();
    }

    setAuthStateResolved(true);
  };

  /**
   * Finalize a fresh login: write the authenticated user atom and clear any leftover
   * persisted impersonation so the new session starts as the real authenticated user.
   */
  const finalizeLoginSession = async (userId: string) => {
    store.set(actAsUserIdAtom, undefined);

    await clearImpersonation();

    setUserId(userId);

    setAuthStateResolved(true);
  };

  const getCurrentAuthUser = async () => {
    try {
      const currentUser = await auth.getCurrentUser();

      return currentUser;
    } catch (e) {
      console.error('[Auth] Error getting current auth user', e);

      Sentry.captureException(e);

      throw e;
    }
  };

  /**
   * Load app user from current authenticated session
   *
   * Retrieves user information from the current auth session and updates global state.
   * Routes to appropriate auth method:
   * - OAuth users: Parse ID token claims directly (instant, no API call). When custom:user_id is missing,
   *   attempts to resolve numeric userId via /v2/users/me. Includes no-downgrade guard to preserve existing
   *   numeric userId if already present. On 401, triggers silent reauth and retries.
   * - Email/password users: Call Cognito API for latest attributes
   */
  const loadCurrentUser = async () => {
    try {
      const idToken = await auth.getIdToken();

      if (!idToken) {
        await finalizeColdLoadSession(undefined);

        return undefined;
      }

      const claims = parseIdTokenClaims(idToken);

      if (!claims) {
        console.error('[Auth] Failed to parse ID token');

        setAuthStateResolved(true);

        return undefined;
      }

      if (isOAuthUser(claims)) {
        const userAttributes = extractUserAttributesFromClaims(claims);

        const customUserId = userAttributes['custom:user_id'];

        const sub = claims.sub;

        const existingUserId = getCurrentUserId();

        const provider = Array.isArray(claims.identities)
          ? (claims.identities[0]?.providerName || '').toLowerCase()
          : undefined;

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Load OAuth user from ID token',
          level: 'info',
          data: {
            hasCustomUserId: !!customUserId,
            provider,
            existingUserId,
          },
        });

        if (customUserId) {
          const parsedCognitoUser = cognitoUserSchema.parse({
            userId: customUserId,
            email: userAttributes.email,
            firstName: userAttributes['custom:first_name'] || '',
            lastName: userAttributes['custom:last_name'] || '',
          });

          await finalizeColdLoadSession(parsedCognitoUser.userId);

          return parsedCognitoUser;
        }

        if (existingUserId && /^\d+$/.test(String(existingUserId))) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'No-downgrade guard: preserving numeric userId',
            level: 'info',
            data: { existingUserId },
          });

          await finalizeColdLoadSession(existingUserId);

          return cognitoUserSchema.parse({
            userId: existingUserId,
            email: userAttributes.email,
            firstName: userAttributes['custom:first_name'] || '',
            lastName: userAttributes['custom:last_name'] || '',
          });
        }

        let resolvedUserId = sub;

        try {
          const currentUser = await getCurrentUserV2({
            Authorization: `Bearer ${idToken}`,
          });

          const apiId = currentUser?.id;

          if (apiId) {
            resolvedUserId = String(apiId);

            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Resolved numeric userId from /v2/users/me',
              level: 'info',
              data: { apiId },
            });
          }
        } catch (err: any) {
          const status = err?.response?.status || err?.status;

          Sentry.addBreadcrumb({
            category: 'auth',
            message: '/v2/users/me lookup failed',
            level: 'warning',
            data: { status, error: err?.message },
          });

          if (status === 401 && typeof window !== 'undefined') {
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Triggering silent reauth after 401',
              level: 'info',
            });

            try {
              await auth.signInWithGoogle();

              const freshToken = await auth.getIdToken();

              if (freshToken) {
                const retryUser = await getCurrentUserV2({
                  Authorization: `Bearer ${freshToken}`,
                });

                const retryApiId = retryUser?.id;

                if (retryApiId) {
                  resolvedUserId = String(retryApiId);

                  Sentry.addBreadcrumb({
                    category: 'auth',
                    message: 'Resolved numeric userId after silent reauth',
                    level: 'info',
                    data: { apiId: retryApiId },
                  });
                }
              }
            } catch (reauthErr) {
              Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Silent reauth failed',
                level: 'error',
                data: { error: (reauthErr as any)?.message },
              });
            }
          }
        }

        const parsedCognitoUser = cognitoUserSchema.parse({
          userId: resolvedUserId,
          email: userAttributes.email,
          firstName: userAttributes['custom:first_name'] || '',
          lastName: userAttributes['custom:last_name'] || '',
        });

        await finalizeColdLoadSession(parsedCognitoUser.userId);

        return parsedCognitoUser;
      }

      try {
        const userAttributes = await auth.fetchUserAttributes();

        if (userAttributes) {
          let userId = userAttributes['custom:user_id'];

          if (!userId) {
            const idTokenForLookup = await auth.getIdToken();

            if (idTokenForLookup) {
              try {
                const currentUser = await getCurrentUserV2({
                  Authorization: `Bearer ${idTokenForLookup}`,
                });

                if (currentUser?.id) {
                  userId = String(currentUser.id);
                }
              } catch (lookupErr) {
                Sentry.addBreadcrumb({
                  category: 'auth',
                  message: '/v2/users/me lookup failed for email/password user',
                  level: 'warning',
                  data: { error: (lookupErr as any)?.message },
                });
              }
            }
          }

          if (!userId) {
            const missingError = new MissingUserIdError({
              sub: claims.sub,
              email: userAttributes.email,
            });

            Sentry.captureException(missingError, {
              tags: { source: 'loadCurrentUser', authMethod: 'password' },
              extra: { sub: claims.sub, email: userAttributes.email },
            });

            throw missingError;
          }

          const parsedCognitoUser = cognitoUserSchema.parse({
            userId,
            email: userAttributes.email,
            firstName: userAttributes['custom:first_name'],
            lastName: userAttributes['custom:last_name'],
          });

          await finalizeColdLoadSession(parsedCognitoUser.userId);

          return parsedCognitoUser;
        }
      } catch (err: any) {
        if (err instanceof MissingUserIdError) {
          throw err;
        }

        console.error('[Auth] Failed to fetch user attributes', err);

        Sentry.captureException(err);

        throw err;
      }

      throw new Error('Unable to load user attributes');
    } catch (e) {
      if (e instanceof MissingUserIdError) {
        setAuthStateResolved(true);

        throw e;
      }

      const parsedError = parseAuthError(e);

      if (isRevokedTokenError(parsedError)) {
        await handleRevokedToken(parsedError, 'loadCurrentUser');
      }

      Sentry.captureException(e);

      setAuthStateResolved(true);
    }
  };

  /**
   * Logs in a user with their email and password — atomic.
   *
   * On `{ type: 'success' }` the userIdAtom is set, the `/v2/users/me` cache is populated,
   * and the user is fully authenticated. Failure variants mean the Cognito session has
   * already been cleaned up via `signOutLocal`, so callers can navigate to /login or stay
   * in place — there is no half-authenticated limbo to recover from.
   *
   * Result variants:
   * - `success` — fully authenticated, atom and cache populated
   * - `newPasswordRequired` — Cognito wants a new password (admin-set first login)
   * - `unsupportedStep` — sign-in returned a nextStep we don't yet support (MFA, etc.)
   * - `transientFailure` — `/v2/users/me` failed twice with 5xx/429/network; user can retry
   * - `persistentFailure` — `/v2/users/me` 4xx (404 user record missing, 401, etc.)
   *
   * Includes a defensive guard for `UserAlreadyAuthenticatedException` (compares the
   * existing session's email to the entered email; same email → fall through, different →
   * signOutLocal + retry). All other errors are captured and re-thrown.
   */
  const login = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<LoginResult> => {
    let response: Awaited<ReturnType<typeof auth.login>> | undefined;

    try {
      response = await auth.login({
        username: email,
        password,
      });
    } catch (e) {
      console.log('[Auth] Password login error:', e);

      if (e instanceof Error && e.name === 'UserAlreadyAuthenticatedException') {
        const recoveryResponse = await recoverFromAlreadyAuthenticated(email, password);

        if (recoveryResponse.kind === 'fallthrough') {
          response = undefined;
        } else {
          response = recoveryResponse.response;
        }
      } else {
        console.error('[Auth] Error logging in', e);

        Sentry.captureException(e);

        throw e;
      }
    }

    const step = response?.nextStep?.signInStep;

    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { type: 'newPasswordRequired' };
    }

    if (step && step !== 'DONE') {
      Sentry.captureMessage('Unsupported Cognito signInStep', {
        level: 'warning',
        tags: { source: 'useAuth.login', signInStep: step },
      });

      return { type: 'unsupportedStep', step };
    }

    const resolved = await resolveProfileAndApplyState();

    if (resolved.ok) {
      return { type: 'success', user: resolved.user };
    }

    // Profile load failed — clean up the Cognito session so the caller doesn't have to
    // worry about a half-authenticated state.
    await auth.signOutLocal();

    return resolved.failure === 'persistent'
      ? { type: 'persistentFailure' }
      : { type: 'transientFailure' };
  };

  /**
   * Report a profile-resolve failure that survived every attempt.
   *
   * Transient failures (rate limit, 5xx, network) are reported at `warning`: the
   * screen shows a retry toast and the person can sign in again, so it is a degraded
   * flow rather than a broken one. A persistent failure leaves the account in a state
   * only support can clear, so it stays at error level.
   */
  const reportResolveFailure = (failed: {
    failure: 'transient' | 'persistent';
    error?: unknown;
    errorMessage?: string;
  }) => {
    Sentry.captureException(failed.error ?? new Error('Login profile resolve failed'), {
      level: failed.failure === 'transient' ? 'warning' : 'error',
      tags: {
        component: 'Login',
        source: 'login_resolve_user',
        resolveFailure: failed.failure,
      },
      extra: {
        errorMessage: failed.errorMessage ?? null,
      },
    });
  };

  /**
   * Resolve `/v2/users/me`, write the auth atoms, and seed the React Query cache.
   *
   * Retries once after `RESOLVE_RETRY_DELAY_MS` for transient failures (5xx/429/network).
   * Persistent 4xx failures fall through immediately. Returns `{ ok: false, failure }` so
   * the caller can map to the appropriate `LoginResult` variant.
   *
   * Atom and cache writes only happen on success — no half-applied state on failure, so
   * the outer `<Login>` doesn't see a userId atom flip and redirect mid-flow.
   *
   * Reporting happens once, after the retry has had its turn, rather than inside each
   * attempt. A rate-limited or 5xx first attempt that the retry clears is the recovery
   * working, so it leaves a breadcrumb; capturing per attempt sent two error events for
   * every failure and reported successes as failures.
   */
  const resolveProfileAndApplyState = async (): Promise<
    { ok: true; user: CognitoUser } | { ok: false; failure: 'transient' | 'persistent' }
  > => {
    const attempt = async (attemptNumber: number) => {
      try {
        const idToken = await auth.getIdToken();

        if (!idToken) {
          return { ok: false as const, failure: 'persistent' as const };
        }

        const currentUser = await getCurrentUserV2({
          Authorization: `Bearer ${idToken}`,
        });

        const apiUserId = currentUser?.id;

        if (!apiUserId) {
          return { ok: false as const, failure: 'persistent' as const };
        }

        const userAttributes = await auth.fetchUserAttributes();

        const cognitoUser = cognitoUserSchema.parse({
          userId: String(apiUserId),
          email: userAttributes.email,
          firstName: userAttributes['custom:first_name'] || '',
          lastName: userAttributes['custom:last_name'] || '',
        });

        await finalizeLoginSession(cognitoUser.userId);

        queryClient.setQueryData(['currentUserV2', cognitoUser.userId], currentUser);

        await queryClient.invalidateQueries({
          queryKey: ['currentUserV2', cognitoUser.userId],
        });

        return { ok: true as const, user: cognitoUser };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'info',
          message: `Profile resolve attempt ${attemptNumber} failed: ${errorMessage}`,
          data: { source: 'login_resolve_user' },
        });

        return { ok: false as const, failure: classifyResolveError(error), error, errorMessage };
      }
    };

    const first = await attempt(1);

    if (first.ok) {
      return first;
    }

    if (first.failure === 'persistent') {
      reportResolveFailure(first);

      return { ok: false as const, failure: first.failure };
    }

    await new Promise((resolve) => setTimeout(resolve, RESOLVE_RETRY_DELAY_MS));

    const second = await attempt(2);

    if (second.ok) {
      return second;
    }

    reportResolveFailure(second);

    return { ok: false as const, failure: second.failure };
  };

  /**
   * Handle `UserAlreadyAuthenticatedException` by comparing the entered email to the
   * existing session's email.
   *
   * Same account → fall through and treat as a successful login (recovers from a UI
   * double-submit race; preserves the previous defensive behavior).
   *
   * Different account → call `signOutLocal` to clear stale tokens, then retry sign-in
   * once. This prevents a stale session from silently logging the user in as the wrong
   * account when they typed credentials for a different one.
   */
  const recoverFromAlreadyAuthenticated = async (
    email: string,
    password: string
  ): Promise<
    { kind: 'fallthrough' } | { kind: 'retried'; response: Awaited<ReturnType<typeof auth.login>> }
  > => {
    let existingEmail: string | undefined;

    try {
      const existingIdToken = await auth.getIdToken();

      if (existingIdToken) {
        const claims = parseIdTokenClaims(existingIdToken);

        existingEmail = claims?.email?.toLowerCase();
      }
    } catch (peekError) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'UserAlreadyAuthenticated: could not read existing session, will retry',
        level: 'warning',
        data: { error: (peekError as any)?.message },
      });
    }

    const enteredEmail = email.toLowerCase();

    if (existingEmail && existingEmail === enteredEmail) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'UserAlreadyAuthenticated: same account, treating as success',
        level: 'info',
      });

      return { kind: 'fallthrough' };
    }

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'UserAlreadyAuthenticated: account mismatch, clearing local session and retrying',
      level: 'warning',
      data: {
        existingEmailKnown: !!existingEmail,
        existingEmailDiffers: existingEmail ? existingEmail !== enteredEmail : undefined,
      },
    });

    await auth.signOutLocal();

    const retryResponse = await auth.login({
      username: email,
      password,
    });

    return { kind: 'retried', response: retryResponse };
  };

  const forgotPassword = (email: string) => {
    return auth.resetPassword({
      username: email,
    });
  };

  const resetPassword = ({
    email,
    verificationCode,
    newPassword,
  }: {
    email: string;
    verificationCode: string;
    newPassword: string;
  }) => {
    return auth.confirmResetPassword({
      username: email,
      confirmationCode: verificationCode,
      newPassword,
    });
  };

  /**
   * Complete a Cognito new-password challenge and finalize the session.
   *
   * Uses the same atomic contract as `login()` — on success the userIdAtom is set and
   * the `/v2/users/me` cache is populated; on failure variants `signOutLocal` has
   * already cleared the session so the caller can navigate freely without a
   * half-authenticated state.
   */
  const setNewPassword = async (newPassword: string): Promise<LoginResult> => {
    await auth.confirmSignIn({ challengeResponse: newPassword });

    const resolved = await resolveProfileAndApplyState();

    if (resolved.ok) {
      return { type: 'success', user: resolved.user };
    }

    await auth.signOutLocal();

    return resolved.failure === 'persistent'
      ? { type: 'persistentFailure' }
      : { type: 'transientFailure' };
  };

  return {
    login,
    signOut: auth.signOut,
    getCurrentAuthUser,
    loadCurrentUser,
    forgotPassword,
    resetPassword,
    setNewPassword,
  };
}
